/**
 * scene.js — the project model and THE renderer for /clone-effect.
 *
 * One renderer, two consumers: the preview canvas and the export encoder call
 * `render()` with nothing different but a scale factor. The whole class of
 * "the downloaded file doesn't look like what I saw" bugs comes from having a
 * second drawing routine, so there isn't one.
 *
 * Scale is passed explicitly instead of being folded into a ctx transform,
 * because two things in this renderer are specified in CANVAS pixels and
 * silently ignore the CTM: `ctx.filter = blur(...)` (mask feather) and the
 * layer canvases' own dimensions. Multiplying coordinates by S keeps preview
 * and export geometrically identical, feather included.
 *
 * Composition model — the clone effect's whole point:
 *
 *   Clip 0 IS the scene. It fills the frame; it has no mask.
 *   Clip 1..N are drawn INTO that same frame, in the same coordinate space,
 *   each showing only through its mask. Nothing is scaled down into a cell and
 *   nothing gets a border — the output has to read as one continuous shot of a
 *   room that happens to contain the same person twice.
 *
 * Exposes window.CloneScene.
 */
(function () {
'use strict';
if (window.CloneScene) return;

var MAX_CLIPS = 4;

/* ─────────────────────────────── model ─────────────────────────────── */

function newClip(file, url, meta) {
    return {
        id: 'c' + Math.random().toString(36).slice(2, 9),
        file: file,
        url: url,
        name: (file && file.name) || '',
        duration: (meta && meta.duration) || 0,
        vw: (meta && meta.width) || 0,
        vh: (meta && meta.height) || 0,

        // timeline
        trimIn: 0,
        trimOut: (meta && meta.duration) || 0,
        start: 0,             // where this clip begins in the composition
        hold: true,           // freeze first/last frame outside its own range

        // layer geometry — nudges for a tripod that was bumped between takes
        dx: 0, dy: 0,         // scene px
        scale: 1,
        fit: 'cover',
        flipH: false,

        // colour match, applied to THIS layer only (see matchTone())
        opacity: 1,           // 1 = solid, below that a see-through double
        exposure: 1,          // multiplier
        tint: [1, 1, 1],      // per-channel gain
        offset: [0, 0, 0],    // per-channel offset (a lifted black level)

        // mask — clip 0 never uses one
        mask: window.CloneMasks.make('line'),

        // audio
        volume: 1,
        muted: false,
        audioOffset: 0,       // fine A/V nudge, seconds (+ = audio later)
        hasAudio: true,

        visible: true
    };
}

function newProject() {
    return {
        W: 1920, H: 1080, fps: 30,
        preset: '16:9',
        bg: '#000000',
        clips: [],
        music: null,          // { file, name, duration, volume, fadeIn, fadeOut, loop, duck, start, trimIn, trimOut }
        quality: 'high',
        // How the finished composition is framed on the way out. 'source' keeps
        // the shape it was shot in; 'vertical' stacks the two performers for
        // Reels and Shorts. `win` holds the two source windows in composition
        // pixels — see reframe.js for where they come from.
        frame: { mode: 'source', win: null }
    };
}

function newMusic(file, meta) {
    return {
        file: file,
        name: (file && file.name) || '',
        duration: (meta && meta.duration) || 0,
        volume: 0.35,
        fadeIn: 1.0,
        fadeOut: 2.0,
        loop: true,
        duck: true,
        duckDepth: 0.75,
        start: 0,
        trimIn: 0,
        trimOut: (meta && meta.duration) || 0
    };
}

/* ─────────────────────────────── timing ─────────────────────────────── */

function clipDuration(c) {
    var d = (c.trimOut || c.duration) - (c.trimIn || 0);
    return Math.max(0, d);
}

function projectDuration(p) {
    var max = 0;
    for (var i = 0; i < p.clips.length; i++) {
        var c = p.clips[i];
        if (!c || !c.visible) continue;
        max = Math.max(max, c.start + clipDuration(c));
    }
    if (max <= 0 && p.music) max = Math.min(60, musicDuration(p.music));
    return max;
}

function musicDuration(m) {
    return Math.max(0, (m.trimOut || m.duration) - (m.trimIn || 0));
}

/**
 * Source time for a clip at composition time t, or null when the clip has
 * nothing to show. `hold` freezes the nearest end instead of dropping to the
 * background — without it a clip that is one frame shorter than its neighbour
 * makes the composite flash to black, which on a clone shot looks like a
 * glitch rather than an edit.
 */
function sourceTimeAt(c, t) {
    var dur = clipDuration(c);
    if (dur <= 0) return null;
    var local = t - c.start;
    if (local < 0) return c.hold ? c.trimIn : null;
    if (local >= dur) return c.hold ? Math.max(c.trimIn, c.trimIn + dur - 1 / 60) : null;
    return c.trimIn + local;
}

function isLive(c, t) {
    var dur = clipDuration(c);
    if (dur <= 0) return false;
    var local = t - c.start;
    return local >= 0 && local < dur;
}

/* ───────────────────────────── geometry ─────────────────────────────
   Where a clip's frame lands inside the scene. `cover` is the default and the
   right one for the clone effect: both takes come off one camera, so they share
   an aspect and cover is a no-op crop — but if the user mixes a portrait phone
   take with a landscape one, cover keeps the frame full instead of pillarboxing
   a seam into the middle of the shot. */

function layerRect(c, sw, sh, W, H) {
    if (!sw || !sh) return { x: 0, y: 0, w: W, h: H };
    var s;
    if (c.fit === 'contain') s = Math.min(W / sw, H / sh);
    else s = Math.max(W / sw, H / sh);
    s *= (c.scale || 1);
    var w = sw * s, h = sh * s;
    return {
        x: (W - w) / 2 + (c.dx || 0),
        y: (H - h) / 2 + (c.dy || 0),
        w: w, h: h
    };
}

/* ───────────────────────────── layer canvases ─────────────────────────────
   Pooled and resized in place. A masked layer needs its own canvas because the
   mask is applied with destination-in: doing that on the composite would erase
   everything already drawn, not just this layer. */

var pool = [];
function scratch(i, w, h) {
    var c = pool[i];
    if (!c) {
        c = pool[i] = {
            cv: document.createElement('canvas'),
            mv: document.createElement('canvas')
        };
        c.ctx = c.cv.getContext('2d');
        c.mctx = c.mv.getContext('2d', { willReadFrequently: false });
    }
    if (c.cv.width !== w || c.cv.height !== h) {
        c.cv.width = w; c.cv.height = h;
        c.mv.width = w; c.mv.height = h;
    }
    return c;
}

function releasePool() { pool.length = 0; }

/* ───────────────────────────── colour match ─────────────────────────────
   Two takes from one camera on auto-exposure are NOT the same brightness: the
   performer standing in a different half of the room moves the metering. The
   seam then reads as a visible vertical step even when the geometry is perfect.
   A per-channel gain on the pasted layer is enough to close it, and align.js
   measures the numbers from the background OUTSIDE the mask. */

function needsTone(c) {
    if (Math.abs((c.exposure || 1) - 1) > 0.004) return true;
    var t = c.tint || [1, 1, 1];
    if (Math.abs(t[0] - 1) > 0.004 || Math.abs(t[1] - 1) > 0.004 || Math.abs(t[2] - 1) > 0.004) return true;
    var o = c.offset || [0, 0, 0];
    return Math.abs(o[0]) > 0.5 || Math.abs(o[1]) > 0.5 || Math.abs(o[2]) > 0.5;
}

/* out = gain·in + offset, per channel, through a 256-entry table.
   The table matters at export size: this runs on every frame of a 1080p layer,
   and three multiply-and-clamps per pixel is 6 M float ops a frame that a
   lookup removes entirely. */
function toneLut(c) {
    var e = c.exposure || 1, t = c.tint || [1, 1, 1], o = c.offset || [0, 0, 0];
    var key = e + '|' + t.join(',') + '|' + o.join(',');
    if (c.__lutKey === key && c.__lut) return c.__lut;
    var lut = [new Uint8ClampedArray(256), new Uint8ClampedArray(256), new Uint8ClampedArray(256)];
    var g = [e * t[0], e * t[1], e * t[2]];
    for (var ch = 0; ch < 3; ch++) {
        for (var v = 0; v < 256; v++) lut[ch][v] = v * g[ch] + o[ch];
    }
    c.__lut = lut; c.__lutKey = key;
    return lut;
}

function applyTone(ctx, c, w, h) {
    if (!needsTone(c)) return;
    var lut = toneLut(c);
    var img;
    try { img = ctx.getImageData(0, 0, w, h); } catch (_) { return; }
    var d = img.data;
    var l0 = lut[0], l1 = lut[1], l2 = lut[2];
    for (var i = 0; i < d.length; i += 4) {
        if (d[i + 3] === 0) continue;
        d[i] = l0[d[i]];
        d[i + 1] = l1[d[i + 1]];
        d[i + 2] = l2[d[i + 2]];
    }
    ctx.putImageData(img, 0, 0);
}

/* ─────────────────────────────── render ───────────────────────────────
 *
 * @param ctx      destination 2D context, sized W*S × H*S
 * @param p        project
 * @param t        composition time, seconds
 * @param getFrame (clipIndex) → CanvasImageSource | null, resolved by the caller
 * @param S        scene→canvas scale (1 for export)
 * @param opts     { mattes: {clipIndex: canvas} }  alpha mattes for 'ai' masks
 */
function render(ctx, p, t, getFrame, S, opts) {
    S = S || 1;
    opts = opts || {};
    var cw = Math.max(1, Math.round(p.W * S));
    var ch = Math.max(1, Math.round(p.H * S));

    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, cw, ch);
    ctx.fillStyle = p.bg || '#000';
    ctx.fillRect(0, 0, cw, ch);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    for (var i = 0; i < p.clips.length; i++) {
        var c = p.clips[i];
        if (!c || !c.visible) continue;
        // Solo: show ONE take, whole, with no mask. The tool's promise is that
        // the two takes line up; checking that needs a way to look at each one
        // alone, and "turn the mask off in your head" is not a way.
        if (opts.solo != null && opts.solo !== i) continue;
        var st = sourceTimeAt(c, t);
        if (st == null) continue;
        var src = getFrame(i, st);
        if (!src) continue;

        var sw = src.displayWidth || src.videoWidth || src.width || c.vw;
        var sh = src.displayHeight || src.videoHeight || src.height || c.vh;
        if (!sw || !sh) continue;

        var r = layerRect(c, sw, sh, cw, ch);
        var masked = opts.solo == null && i > 0 && c.mask && c.mask.type !== 'none';
        // A translucent take has to be flattened on its own canvas first:
        // fading the source as it is drawn would let the mask's feathered edge
        // fade twice and leave a darker rim along the seam.
        var alpha = (c.opacity == null || opts.solo != null) ? 1 : Math.max(0, Math.min(1, c.opacity));
        var needsOwnCanvas = masked || needsTone(c) || alpha < 1;

        if (!needsOwnCanvas) {
            drawSource(ctx, src, r, c);
            continue;
        }

        var sc = scratch(i, cw, ch);
        sc.ctx.setTransform(1, 0, 0, 1, 0, 0);
        sc.ctx.globalCompositeOperation = 'source-over';
        sc.ctx.filter = 'none';
        sc.ctx.clearRect(0, 0, cw, ch);
        sc.ctx.imageSmoothingEnabled = true;
        sc.ctx.imageSmoothingQuality = 'high';
        drawSource(sc.ctx, src, r, c);
        applyTone(sc.ctx, c, cw, ch);

        if (masked) {
            var live = window.CloneMasks.resolveAt(c.mask, t);
            if (live.type === 'ai') {
                var matte = opts.mattes && opts.mattes[i];
                if (matte) {
                    sc.ctx.globalCompositeOperation = 'destination-in';
                    // The matte is authored at the clip's own frame size, so it
                    // has to land on exactly the rect the frame landed on.
                    sc.ctx.drawImage(matte, r.x, r.y, r.w, r.h);
                    sc.ctx.globalCompositeOperation = 'source-over';
                }
                // No matte yet (model still loading) → the layer shows in full
                // rather than vanishing: a missing mask should look unfinished,
                // not look like the clip failed to load.
            } else {
                window.CloneMasks.paint(sc.mctx, live, cw, ch, S);
                sc.ctx.globalCompositeOperation = 'destination-in';
                sc.ctx.drawImage(sc.mv, 0, 0);
                sc.ctx.globalCompositeOperation = 'source-over';
            }
        }

        var prevAlpha = ctx.globalAlpha;
        ctx.globalAlpha = alpha;
        ctx.drawImage(sc.cv, 0, 0);
        ctx.globalAlpha = prevAlpha;
    }

    ctx.restore();
}

function drawSource(ctx, src, r, c) {
    if (c.flipH) {
        ctx.save();
        ctx.translate(r.x + r.w, r.y);
        ctx.scale(-1, 1);
        ctx.drawImage(src, 0, 0, r.w, r.h);
        ctx.restore();
    } else {
        ctx.drawImage(src, r.x, r.y, r.w, r.h);
    }
}

/* ─────────────────────────── output framing ────────────────────────────
   Preview and export must show the SAME picture, so the reframe lives here and
   both go through this one door. Anything that computed the vertical stack
   twice would eventually compute it two different ways, and the user would
   discover the difference only in the finished file.

   See reframe.js for why the stack is the shape and where its numbers come
   from. */

var frameScratch = null;

function renderFramed(ctx, p, t, getFrame, S, opts) {
    var R = window.CloneReframe;
    var f = p && p.frame;
    if (!R || !f || f.mode !== 'vertical') { render(ctx, p, t, getFrame, S, opts); return; }

    var bw = Math.max(2, Math.round(p.W * S)), bh = Math.max(2, Math.round(p.H * S));
    if (!frameScratch) frameScratch = document.createElement('canvas');
    if (frameScratch.width !== bw || frameScratch.height !== bh) {
        frameScratch.width = bw; frameScratch.height = bh;
    }
    var sctx = frameScratch.getContext('2d');
    sctx.clearRect(0, 0, bw, bh);
    render(sctx, p, t, getFrame, S, opts);

    // Fewer than two takes: there is nothing to stack, and stacking the same
    // person twice would look like a bug rather than an effect. A draft can
    // reopen in vertical mode and then lose a take, so this is reachable.
    if (p.clips.length < 2) {
        var oa = ctx.canvas.width / ctx.canvas.height;
        var sw = Math.min(bw, bh * oa), sh = sw / oa;
        ctx.save();
        ctx.fillStyle = p.bg || '#000';
        ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        ctx.drawImage(frameScratch, (bw - sw) / 2, (bh - sh) / 2, sw, sh,
            0, 0, ctx.canvas.width, ctx.canvas.height);
        ctx.restore();
        return;
    }

    var blanks = [];
    for (var bi = 0; bi < p.clips.length; bi++) blanks.push(null);
    var win = (f.win && f.win.length === p.clips.length)
        ? f.win
        : R.windowsFor(blanks, p.W, p.H,
            R.cellAspect(R.outputSize(p), p.clips.length),
            (p.clips[1] && p.clips[1].mask && p.clips[1].mask.angle) || 0,
            (p.clips[1] && p.clips[1].mask && p.clips[1].mask.pos) || 0.5);

    var scaled = win.map(function (w) {
        return { x: w.x * S, y: w.y * S, w: w.w * S, h: w.h * S };
    });
    R.drawVertical(ctx, frameScratch, scaled, ctx.canvas.width, ctx.canvas.height, p.bg);
}

function releaseFrameScratch() { frameScratch = null; }

window.CloneScene = {
    MAX_CLIPS: MAX_CLIPS,
    newProject: newProject,
    newClip: newClip,
    newMusic: newMusic,
    clipDuration: clipDuration,
    projectDuration: projectDuration,
    musicDuration: musicDuration,
    sourceTimeAt: sourceTimeAt,
    isLive: isLive,
    layerRect: layerRect,
    render: render,
    releasePool: releasePool,
    renderFramed: renderFramed,
    releaseFrameScratch: releaseFrameScratch,
    needsTone: needsTone
};
})();
